import useOpenable from "@Front/Hooks/useOpenable";
import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent, MutableRefObject } from "react";

import { IOption } from "./DropdownMenu/DropdownOption";

type UseDropdownControllerProps = {
	options: IOption[];
	selectedOptionProps?: IOption | null;
	onSelectionChange?: (option: IOption) => void;
	disabled?: boolean;
	label?: string;
	placeholder?: string;
};

type UseDropdownControllerReturn = {
	selectedOption: IOption | null;
	openable: ReturnType<typeof useOpenable>;
	labelIdRef: MutableRefObject<string>;
	menuIdRef: MutableRefObject<string>;
	handleOnSelect: (newOption: IOption, _options: IOption[]) => void;
	handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
	ariaLabel: string;
};

export function useDropdownController(props: UseDropdownControllerProps): UseDropdownControllerReturn {
	const { selectedOptionProps, onSelectionChange, disabled, label, placeholder } = props;
	const [selectedOption, setSelectedOption] = useState<IOption | null>(selectedOptionProps ?? null);
	const openable = useOpenable({ defaultOpen: false });
	const labelIdRef = useRef<string>(`dropdown-label-${Math.random().toString(36).substr(2, 9)}`);
	const menuIdRef = useRef<string>(`dropdown-menu-${Math.random().toString(36).substr(2, 9)}`);

	useEffect(() => {
		setSelectedOption(selectedOptionProps ?? null);
	}, [selectedOptionProps]);

	const handleOnSelect = useCallback(
		(newOption: IOption, _options: IOption[]) => {
			setSelectedOption(newOption);
			onSelectionChange?.(newOption);
		},
		[onSelectionChange],
	);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (disabled) return;
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				openable.toggle();
			} else if (event.key === "Escape" && openable.isOpen) {
				event.preventDefault();
				openable.close();
			}
		},
		[disabled, openable],
	);

	const ariaLabel = label || placeholder || "Dropdown";

	return {
		selectedOption,
		openable,
		labelIdRef,
		menuIdRef,
		handleOnSelect,
		handleKeyDown,
		ariaLabel,
	};
}
